/**
 * The Chat42 web app.
 *
 * Reference consumer of `@chat42/react` (PRD B7a). Anything it can do that a
 * host embedding the package cannot is a defect — which is why the sign-in
 * lives here, in the app, rather than inside the components: a host embedding
 * Chat42 already has its own session and should not be sent through ours.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createRoot } from 'react-dom/client';
import {
  Chat42Client,
  fetchAuthConfig,
  completeSignIn,
  beginSignIn,
  loadSession,
  clearSession,
  tokenSource,
  gatewayTokenFor, mcpTokenFor, workTokensFor,
  keepTokensFresh, signInAgain, signOut, onSignedOut, finishSignInPopup, authProblem, onAuthProblem,
  returnedWithNewAccess, renewAfterAccessChange, ACCESS_CHANGED,
  type AuthConfig,
} from '@chat42/client';
import {
  Boundary, Chat42, Chat42Mark, DiagramFontsCtx, FailureSinkCtx, FocusCtx, HostActionCtx, PrintDocumentCtx, SandboxDocumentCtx,
  FolderPicker, PAIR_PATH, attachHostCode,
  attachRecordRepository, createReporter, focusForWork, frameErrorMessage, frameSurfaces, framedClientOptions,
  askHostWork, followAsks, hostConnecting, hostToken, readsCodeOnlyOf, keepAboutCurrentForWork, openSessionAboutRecord,
  closedState, openGate, recordRepositories, reopenStep, reopenStoredSession, workingProjectOf,
  attentionMessage, sessionChangedMessage, setHostBearer, standingKeeper, turnMoved, windowFailure,
  type Failure, type FolderPickOutcome, type HostAction, type HostActionAnswer, type HostRequest, type HostToken, type RepositoryName,
} from '@chat42/react';
import { Chat42FrameBridge, FRAME_FEATURES, type ChatView, type DetailOpens, type FrameMessage, type PickFolder, type ReplyCost, type StartWork } from '@chat42/embed';

/**
 * Three files this app serves, which the components are told about because they
 * cannot know what the page they are on has beside it.
 *
 * The first is the stylesheet declaring the fonts a diagram is set in. Only
 * its address is here: it is fetched when a diagram is first drawn, and the
 * font files after it.
 *
 * The second is the document a sandboxed frame is loaded from. The server
 * sends this app with a content security policy that runs no inline script,
 * and a frame filled from `srcdoc` inherits it, so a connector's view or a
 * page a model wrote would run nothing. Loaded from this address they run
 * under the document's own policy, with no origin
 * (apps/server/src/content-security-policy.ts). Absolute, because a panel
 * popped out into its own window has a blank address to resolve against.
 *
 * The third is the document a PDF export's print view is opened at. It is
 * served the same way, with no origin, so a page a model wrote can draw
 * itself there to be printed and can reach nothing of this app's.
 */
/**
 * Who may frame this app, and therefore who may hand it a token and drive it.
 *
 * Asked of the server, never of the embedder: a list supplied by the host would be the host
 * vouching for itself. It is the same list the server puts in `frame-ancestors`, so an origin
 * the browser would refuse to let frame this app is also one the bridge refuses to answer.
 */
async function framedBy(): Promise<string[]> {
  try {
    const res = await fetch('/api/embedding');
    if (!res.ok) return [];
    const body = (await res.json()) as { frameAncestors?: string[] };
    return body.frameAncestors ?? [];
  } catch {
    return [];
  }
}

/** In a frame at all. `window.parent === window` at the top level. */
const isFramed = (): boolean => {
  try { return window.parent !== window; } catch { return true; }
};

/**
 * The commit this bundle was built from, put here by `build.mjs`, for failure reports (ADR 0100).
 * Read with `typeof` so a bundle built some other way, without the definition, says nothing
 * rather than failing to start.
 */
declare const __CHAT42_COMMIT__: string | undefined;
const COMMIT = typeof __CHAT42_COMMIT__ === 'string' && __CHAT42_COMMIT__ ? __CHAT42_COMMIT__ : undefined;

/**
 * Failures nobody's boundary saw: an error thrown to the top of the page, and a promise nobody
 * handled. Sent with the same report a boundary sends, through whichever client is signed in
 * now, so they reach the journal too (ADR 0100). Module state rather than React state, because
 * the listeners outlive any one render and the outermost boundary sits above the component that
 * holds the client.
 *
 * Before sign-in there is nobody to file a report under, and the server accepts none without a
 * token, so those are dropped; a failure that early is on the screen as the page's own fallback.
 */
let reportingClient: Chat42Client | null = null;
/** The session a framed page is showing, for a report that did not come from inside one. */
let shownSessionId: string | undefined;
const reportPageFailure = createReporter(
  (report) => reportingClient?.reportClientError(report),
  { ...(COMMIT ? { commit: COMMIT } : {}), userAgent: navigator.userAgent },
);
window.addEventListener('error', (event) => {
  const failure = windowFailure({ type: 'error', message: event.message, error: event.error }, shownSessionId);
  if (failure) reportPageFailure(failure);
});
window.addEventListener('unhandledrejection', (event) => {
  const failure = windowFailure({ type: 'unhandledrejection', reason: event.reason }, shownSessionId);
  if (failure) reportPageFailure(failure);
});

const DIAGRAM_FONTS = `${window.location.origin}/dist/diagram-fonts.css`;
const SANDBOX_DOCUMENT = `${window.location.origin}/static/sandbox.html`;
const PRINT_DOCUMENT = `${window.location.origin}/static/print.html`;

/** What the host asked a session for: its title, and the model and origin where it sent them. */
interface SessionRequest {
  title: string;
  model?: string;
  origin?: string;
  /** Start over: the conversation on screen, which the new one replaces under the origin (ADR 0137). */
  replace?: string;
}

/** Work held until a session is ready to take it, then handed to the conversation's line (ADR 0137). */
interface HeldWork {
  work: StartWork;
  /** The host's `start-work`, or the conversation told of a run it did not know (ADR 0108). */
  from: 'host' | 'project';
  run?: string;
  repository?: string;
}

/** How often asks this frame waits to hear of are read again where no change is seen: a backstop. */
const FOLLOW_ASKS_MS = 15_000;

type State =
  | { phase: 'starting' }
  /** Framed, and waiting for the host to say who the person is. */
  | { phase: 'awaiting-host' }
  | { phase: 'signed-out'; config: AuthConfig }
  | { phase: 'signed-in'; client: Chat42Client }
  | { phase: 'open' }               // no issuer configured; no sign-in needed
  | { phase: 'failed'; message: string };

function App() {
  const [state, setState] = useState<State>({ phase: 'starting' });
  /** The session the host asked for, and the bridge it asked through. */
  const [hostSessionId, setHostSessionId] = useState<string | undefined>();
  /**
   * What the host wants shown and how it should look.
   *
   * Held in state, not read once and dropped. A host embedding this decides which surfaces its
   * panel has — a session list inside somebody else's product is an invitation to wander off —
   * and the frame honouring `init` only for its token is the frame ignoring the arrangement.
   */
  const [hostSurfaces, setHostSurfaces] = useState<Record<string, boolean> | undefined>();
  const [hostTheme, setHostTheme] = useState<Record<string, string> | undefined>();
  /**
   * How the host wants turns drawn, and who answers in its words (ADR 0142). `init` says it first,
   * `set-view` changes it; a redraw, never a restart. Absent, the frame draws every step as before.
   */
  const [hostView, setHostView] = useState<ChatView | undefined>();
  const [hostAssistant, setHostAssistant] = useState<{ name: string; monogram: string } | undefined>();
  /** How the host wants each reply's cost shown (ADR 0145): `init` first, `set-reply-cost` after. */
  const [hostReplyCost, setHostReplyCost] = useState<ReplyCost | undefined>();
  /**
   * What a reply's "Show the detail" does (ADR 0146): opens that turn, or, where the host said
   * `view`, asks the host for full detail, which it switches to and remembers.
   */
  const [hostDetail, setHostDetail] = useState<DetailOpens>('turn');
  // What the person's next message is about, as the host said it (Rule42 Plan 16, C2): the chip above the box.
  const [hostFocus, setHostFocus] = useState<{ title: string } | null>(null);
  /**
   * A session the host asked to be created, as state rather than a ref.
   *
   * It has to re-render: the effect that does the creating runs on state, and `new-session`
   * usually arrives just after `init` has already produced the client — so a ref set at that
   * moment changes nothing and no session is ever made.
   */
  const [wanted, setWanted] = useState<SessionRequest | null>(null);
  /**
   * Where the host's request for a session has got to, for the empty screen: "Connecting…"
   * while it is on its way, so nothing offers to start a second one (ADR 0097). See
   * `hostConnecting`.
   */
  const [hostRequest, setHostRequest] = useState<HostRequest>('none');
  /** A model the host asked the open session to move to, held until there is one and it is not mid-turn. */
  const [pendingModel, setPendingModel] = useState<string | null>(null);
  /**
   * Where the person is in the host's page, as it last said. Wrapped, so the same place said
   * again is a new value and is sent again; and held, so a session that becomes ready later is
   * told the place the person was already in.
   */
  const [location, setLocation] = useState<{ context: Record<string, string> } | null>(null);
  /**
   * The host's open request for a folder, while the picker is up (ADR 0105). One at a time: a
   * second request answers the first as cancelled, so every request still gets exactly one
   * answer and the person sees one picker, the one asked for last.
   */
  const [picking, setPicking] = useState<Omit<PickFolder, 'type'> | null>(null);
  /**
   * Whether the host makes the person's own writes (`init`'s `personActions`, ADR 0147), and each
   * press waiting on its answer by request id. A press nobody answers is answered as lost when the
   * frame goes, so a card never spins for good.
   */
  const [hostPersonActions, setHostPersonActions] = useState(false);
  const actionWaits = useRef(new Map<string, (answer: HostActionAnswer) => void>());
  /** The mode the host's setting gives the open session (ADR 0147), held until there is one. */
  const [pendingMode, setPendingMode] = useState<'manual' | 'auto' | null>(null);
  /** The same, for the bridge's callback, which outlives the render that made it. */
  const pickingRef = useRef<Omit<PickFolder, 'type'> | null>(null);
  /** Locations are sent one after another, so a slow one never lands over the one after it. */
  const locationChain = useRef<Promise<unknown>>(Promise.resolve());
  /**
   * What the host has been told about each session, and what is known of where it stands.
   * Refs, because they are read by callbacks the bridge and the component hold, and changing
   * them should not re-render anything.
   */
  const shownRef = useRef<{ id: string; title: string; state: string } | null>(null);
  // The latest answer wins: reads on open, on each turn and after start-work overlap. See `standingKeeper`.
  const standingRef = useRef(standingKeeper());
  const lastStateRef = useRef(new Map<string, string>());
  /** Bumped as a turn ends, so a model change held back for it is tried again then. */
  const [turnEnds, setTurnEnds] = useState(0);
  /**
   * Asks this frame handed to a conversation's line that wait their turn, by id, with the place the
   * host was last told (ADR 0137). Only so the host hears when each starts while the frame is open:
   * the line itself is on the server, and an ask goes on waiting there, and starts, whatever
   * becomes of this frame.
   */
  const waitingAsksRef = useRef(new Map<string, { sessionId: string; position: number }>());
  /** Bumped when an ask starts waiting, so the backstop read runs while any do. */
  const [asksWaiting, setAsksWaiting] = useState(0);
  // An id the host asked for, held until it can be checked. Separate from `hostSessionId`
  // because an id this frame just minted needs no checking, and probing it would race the
  // creation it came from.
  const [openRequest, setOpenRequest] = useState<string | null>(null);
  /** What the open request said to make the session again with, if its id is gone. */
  const openMoreRef = useRef<{ model?: string; origin?: string; readOnly?: true }>({});
  /**
   * The session on screen was opened read only (ADR 0135): it is shown and nothing is written to
   * it, so a model, a place or work the host sends while it is open is refused, not acted on.
   */
  const readOnlyRef = useRef(false);
  /** Holds back a finished conversation's report while an ordinary open is being decided (ADR 0151). */
  const openGateRef = useRef(openGate());
  useEffect(() => { if (hostRequest !== 'pending') openGateRef.current.settle(); }, [hostRequest]);
  /**
   * The same, for drawing: a session open read only has no message box (ADR 0146). A host that keeps
   * one frame and switches it between a person's own conversation and a run they are watching cannot
   * turn the box off with `surfaces`, which `init` alone sets.
   */
  const [readOnlyOpen, setReadOnlyOpen] = useState(false);
  /**
   * A stored id that could not be checked, and why. Shown with a retry in place of the
   * conversation, because nobody knows whether that session is there: opening it would show a
   * conversation that may not load, and making a new one would replace one that may be fine.
   */
  const [reopenFailed, setReopenFailed] = useState<{ id: string; message: string } | null>(null);
  /**
   * Work the host asked to start, held until a session is ready to take it — and the session
   * that is: opened or made, with its record's code attached or given up on. Kept apart from
   * `hostSessionId`, which is set the moment a request arrives, before anybody knows whether
   * that session exists or has its code; a run started then would take its first turn without
   * the repository it is about. Every piece is kept, in the order asked, and handed to the
   * conversation's line on the server once the session is ready (ADR 0137): a second `start-work`
   * that arrived before then used to replace the first.
   */
  const heldWorkRef = useRef<HeldWork[]>([]);
  /** Bumped when work is held, so the handing over looks again. */
  const [workHeld, setWorkHeld] = useState(0);
  /** Work is handed over one piece after another, so the line takes it in the order it was asked. */
  const handingRef = useRef<Promise<void>>(Promise.resolve());
  const [readySessionId, setReadySessionId] = useState<string | null>(null);
  const bridgeRef = useRef<Chat42FrameBridge | null>(null);
  /** What the host said this frame is about, kept until a session exists to carry it. */
  const entryRef = useRef<Record<string, string>>({});
  /**
   * The same, as state, for what the screen shows of it: the names the record gives its
   * repositories, which the Where chip shows a conversation's code by. A ref alone would leave
   * a list renamed on the record showing the old names until something else re-rendered.
   */
  const [entry, setEntry] = useState<Record<string, string>>({});
  const repositoryNames = useMemo<RepositoryName[]>(
    () => recordRepositories(entry).repositories
      .filter((r) => r.label)
      .map((r) => ({ url: r.url, label: r.label! })),
    [entry],
  );
  /** The project a PRD in one works from, for the line under the box (ADR 0136); undefined otherwise. */
  const workingProject = useMemo(() => workingProjectOf(entry), [entry]);
  /** Sessions whose record's code is being attached by this frame right now. */
  const attachingRef = useRef(new Set<string>());
  /**
   * The host's token, as it last handed one over. Read by the framed client on every request
   * and by every Rule42 gateway on the page, and replaced by `chat42:token`; see `hostToken`.
   */
  const hostTokenRef = useRef<HostToken | null>(null);

  const build = useCallback((config: AuthConfig | null) => {
    const client = new Chat42Client({
      baseUrl: window.location.origin,
      token: tokenSource(config),
      // A second, gateway-scoped token from the same grant. The issuer keeps the
      // audiences apart deliberately, so one token cannot serve both.
      gatewayToken: () => gatewayTokenFor(config),
      mcpToken: () => mcpTokenFor(config),
      // Both at once, through one renewal — see auth.ts on rotating refresh tokens.
      workTokens: (force) => workTokensFor(config, force),
      // In a popup, so whatever somebody was doing is still there afterwards.
      ...(config ? { signInAgain: () => signInAgain(config) } : {}),
      // Offered by the app because signing in is the app's. A host embedding
      // Chat42 leaves this out and keeps its own sign-out. With an end-session
      // endpoint the page is already leaving for the issuer when this resolves;
      // without one, the signed-out screen is where it lands.
      ...(config ? {
        signOut: async () => {
          await signOut(config);
          setState({ phase: 'signed-out', config });
        },
        // What that sign-out actually reaches, from the issuer's own discovery
        // document. The screen that offers it says which of the two happened,
        // because "signed out" means something different at an issuer with no
        // revocation and no end-session endpoint — and the difference is the
        // one a person needs before they leave a shared machine.
        signOutReach: {
          revokesRefreshToken: Boolean(config.revocationEndpoint),
          endsIssuerSession: Boolean(config.endSessionEndpoint),
        },
      } : {}),
      // The client never navigates the page itself — a component that redirects
      // its host out from under it is not embeddable. It reports, we decide.
      onUnauthenticated: () => {
        clearSession();
        setState(config ? { phase: 'signed-out', config } : { phase: 'open' });
      },
    });
    if (config) {
      // Renewed ahead of expiry and handed to the server, so a turn that runs
      // for an hour never presents a token that ran out at minute fifteen.
      keepTokensFresh(config, (tokens) => void client.offerTokens(tokens));
      client.setSignInProblem(authProblem());
      onAuthProblem((problem) => client.setSignInProblem(problem));
      // Signing out in one tab signs out all of them. The session in shared
      // storage is already gone, so the only question is whether this tab says
      // so now or at its next request's 401 — and a tab that still looks signed
      // in after somebody signed out is the one that gets used by mistake.
      onSignedOut(() => setState({ phase: 'signed-out', config }));
    }
    return client;
  }, []);

  /**
   * The host bridge, when this app is framed by one.
   *
   * Mounted before the sign-in effect and skipped entirely at the top level, so nothing about
   * the ordinary app changes: a page nobody frames never starts a bridge, and a frame nobody
   * allowed never accepts a message.
   *
   * The host supplies the token. This app's own sign-in is for people who came to it directly —
   * a product embedding Chat42 already knows who the person is, and sending them through a
   * second sign-in inside a frame is both worse and, in a sandboxed frame, often impossible.
   */
  /**
   * A stored session id can outlive its session.
   *
   * A host persists the id so the next open resumes the same conversation, and nothing tells it
   * when that session is deleted or when the id was minted under another organisation — both of
   * which answer 404 here, deliberately, so that a cross-tenant probe cannot confirm an id
   * exists. Left alone the frame parks on a conversation that will never load and says nothing,
   * which reads as a panel that is broken rather than one asking for a session it cannot have.
   *
   * So: check, and on a miss fall through to creating one. The title comes from entry context
   * because `open-session` carries only an id — the host already said what this conversation is
   * about, and asking it again would need a protocol round trip to learn what is already here.
   *
   * A miss is the server saying 404 and nothing else. A check that failed (a 500, a refused
   * token, a timeout) used to count as one, so a brief outage replaced a host's valid session
   * with an empty one. It now creates nothing: the host is told, and the frame offers a retry.
   * See `reopenStoredSession`.
   *
   * On a hit, the session gets the record's code if it has none. Only creating used to attach
   * it, so a session made before its record named a repository reopened without the code for
   * good. The conversation is already on screen by then — `hostSessionId` was set when the
   * request arrived — and stays there whatever the attach does; a reason it could not be
   * attached goes to the host as a creation's does. A session that already has code is kept to
   * the record's Read-only setting instead, which may have changed since the code arrived, and a
   * setting that could not be kept goes to the host the same way. A record that links several
   * repositories has the ones its session is missing added beside the ones it has (ADR 0088).
   * See `attachRecordRepository`.
   */
  useEffect(() => {
    if (state.phase !== 'signed-in' || openRequest === null) return;
    const id = openRequest;
    setOpenRequest(null);
    setReopenFailed(null);
    void (async () => {
      const stored = await reopenStoredSession(state.client, id);
      const readOnly = openMoreRef.current.readOnly === true;
      // A finished one is reached past, to the live one under the record's name (ADR 0151).
      const closed = stored.kind === 'found' && closedState((stored.session as { state?: unknown }).state);
      const step = reopenStep(stored.kind === 'failed' ? 'failed' : stored.kind === 'absent' ? 'absent' : 'found', readOnly, closed);
      if (stored.kind === 'failed') {
        setReopenFailed({ id, message: stored.message });
        setHostRequest('done');
        bridgeRef.current?.emit(frameErrorMessage(stored.message));
        return;
      }
      if (step === 'missing') {
        // A replay of a session that is not there: nothing is made (ADR 0135). The host says so.
        setHostSessionId(undefined);
        setHostRequest('done');
        bridgeRef.current?.emit({ type: 'chat42:session-missing', sessionId: id });
        return;
      }
      if (step === 'show') {
        // A replay: shown as it is. No about, no code attached, nothing started.
        setReadySessionId(id);
        setHostRequest('done');
        return;
      }
      if (stored.kind === 'absent' || step === 'create') {
        // Still on its way, or finished: made now, as `new-session` would make it, with the model
        // and the origin `open-session` carried where the host sent them. With the origin this is
        // find-or-create, so a live session the person has under it is the one opened.
        setHostSessionId(undefined);
        setWanted({ title: entryRef.current['title'] ?? '', ...openMoreRef.current });
        return;
      }
      const session = stored.session;
      // One attach per session at a time. A host that sends `open-session` twice in quick
      // succession would otherwise ask twice before the first raise was on the record, and the
      // runtime could open the same code twice.
      if (attachingRef.current.has(id)) return;
      attachingRef.current.add(id);
      try {
        // What it is about, as the host says it now, before the session is ready for work: a
        // stored session was made with the record as it was described then, and without this a
        // PRD conversation made before the host named its organisation would never learn it
        // (ADR 0101). Never throws; the runtime writes it only where it differs. A project's
        // conversation told of a run it did not know starts working it (ADR 0112).
        const work = await keepAboutCurrentForWork(state.client, id, entryRef.current, (session as { model?: string }).model);
        if (work) startProjectWork(work);
        const { unattached, unsettled, trouble } = await attachRecordRepository(state.client, session, entryRef.current);
        // One message, not two: the host's panel shows the last it was sent, so a second
        // replaced the first, and with several repositories there can be a line for each.
        const message = [unattached, unsettled].filter(Boolean).join('\n');
        if (message) bridgeRef.current?.emit(frameErrorMessage(message, trouble));
      } finally {
        attachingRef.current.delete(id);
        setReadySessionId(id);
        setHostRequest('done');
      }
    })().catch(() => undefined);
  }, [state, openRequest]);

  /**
   * Make the session a host asked for, and report it — or report why not.
   *
   * No work item. A work item in Rule42's profile is a plan to execute: binding one pins that
   * plan's directive and makes the session its execution, with a notification when it closes.
   * A panel opened on a PRD bound the unit's id that way, Rule42 had no plan by that id, and
   * every open left a session behind and answered 500. A plan's panel would have bound
   * correctly and been worse — opening a chat about a plan should not start carrying it out. The
   * conversation is about the record, named for it; it is not the record's execution.
   *
   * It does know which record, though. The entry context goes with the request as `about`, which
   * the model is given from its first turn as context and never as instruction, with the record's
   * toolsets as the session's focus — so "summarise this" is understood, and the PRD is read over
   * the Rule42 connection by its id without anybody saying which. See `sessionAboutRecord` and
   * ADR 0075.
   *
   * Where the record names a repository, the session is created with that code attached, which
   * waits for the clone — "Starting…" covers it. Code that cannot be attached does not cost the
   * conversation: it is made without, and the host is told why, so its panel can say so.
   *
   * Returns a promise so the screen that asked can say what happened. A request answered only
   * through the host left the frame showing "Starting…" forever when it failed.
   */
  // One create at a time. The host's request and the empty screen's own start can both ask
  // within the same second — the stored session gone, so the frame makes one, while "always start
  // one" or a press on the button makes another — and each made a session. Three for one PRD in
  // six seconds, the run in the first and the screen showing the last, empty, one. A second ask
  // while one is in flight now waits for that one and gets the same session.
  const creatingRef = useRef<Promise<void> | null>(null);
  /** Hold a piece of work until the session is ready, then hand it to the conversation's line. */
  const holdWork = (held: HeldWork): void => {
    heldWorkRef.current.push(held);
    setWorkHeld((n) => n + 1);
  };
  /**
   * A project's conversation told of an analysis run or a policy draft it did not know (ADR
   * 0108): the press in Rule42's dialog that named it, so it is started as that press's
   * `start-work` would be, through the same path, once the session is ready. Two asks for one
   * run are one: the conversation's line on the server keeps the host's, the more specific ask,
   * with a report's own scoped brief (`mergeAsks`), so two first messages are never two runs at
   * the same work.
   */
  const startProjectWork = (work: { prompt: string; permissionMode: string; run: string; repository?: string }): void => {
    holdWork({
      from: 'project', run: work.run, ...(work.repository ? { repository: work.repository } : {}),
      work: { type: 'chat42:start-work', prompt: work.prompt, permissionMode: work.permissionMode },
    });
  };
  const createForHost = useCallback((request: SessionRequest): Promise<void> => {
    if (creatingRef.current) return creatingRef.current;
    const creating = createOnce(request).finally(() => {
      creatingRef.current = null;
      setHostRequest('done');
    });
    creatingRef.current = creating;
    return creating;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state]);
  /**
   * With an origin, find-or-create (ADR 0097): this person's session for the host's scope is
   * opened if there is one, without asking a git host anything first, and made under it if not.
   * With a model, the session is made on it. See `openSessionAboutRecord`.
   */
  const createOnce = async ({ title, model, origin, replace }: SessionRequest): Promise<void> => {
    if (state.phase !== 'signed-in') throw new Error('Not signed in yet.');
    try {
      const { session, unattached, unsettled, trouble, work } = await openSessionAboutRecord(
        state.client, entryRef.current, title,
        { ...(model ? { model } : {}), ...(origin ? { origin } : {}), ...(origin && replace ? { replace } : {}) },
      );
      // Shown even when it was made and then stopped: a failed session says why, and is an id
      // the host can keep rather than a reason to make another.
      setHostSessionId(session.id);
      setReadySessionId(session.id);
      // The host asked for a new one after a stored id could not be checked: that is its call.
      setReopenFailed(null);
      if (work) startProjectWork(work);
      const said = [unattached, unsettled].filter(Boolean).join('\n');
      if (said) bridgeRef.current?.emit(frameErrorMessage(said, trouble));
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Could not create a session.';
      bridgeRef.current?.emit(frameErrorMessage(message));
      throw err instanceof Error ? err : new Error(message);
    }
  };

  /**
   * Ask where a session stands and tell the host (ADR 0097): the model its next turn runs on,
   * after every fallback, and how many times the person has spoken. Told with the session as
   * the host last saw it; where the component has not shown it yet, its own report will carry
   * what is learned here. A failed read tells the host nothing new rather than something wrong.
   */
  const refreshStanding = useCallback(async (client: Chat42Client, id: string): Promise<void> => {
    const keep = standingRef.current.begin(id);
    try {
      // An answer overtaken by a later read, or by what start-work said, is dropped unsaid.
      if (!keep(await client.sessionStanding(id))) return;
    } catch {
      return;
    }
    const shown = shownRef.current;
    if (shown?.id === id) bridgeRef.current?.emit(sessionChangedMessage(shown, standingRef.current.get(id)));
  }, []);

  /**
   * Which session is shown, told to the host with what is known of where it stands. A turn
   * starting or ending is when that moves, so it is asked for again then.
   */
  /** "Show the detail" under `set-detail` `view`: the host switches the view (ADR 0146). Nothing changes here. */
  // The chip's ×: gone here at once, and the host told, which drops the thing from what it tells the next turn.
  const focusChip = useMemo(() => (hostFocus ? {
    title: hostFocus.title,
    clear: () => { setHostFocus(null); bridgeRef.current?.emit({ type: 'chat42:focus-cleared' }); },
  } : null), [hostFocus]);
  const askForDetail = useCallback(() => {
    const shown = shownRef.current;
    if (shown) bridgeRef.current?.emit({ type: 'chat42:detail-asked', sessionId: shown.id });
  }, []);
  const onSessionChanged = useCallback((session: { id: string; title: string; state: string }) => {
    // A finished stored conversation looked up on the way to the live one is not the answer: Rule42
    // would keep its id. Not reported while the open is being decided (ADR 0151).
    if (!openGateRef.current.allows(session)) return;
    const was = lastStateRef.current.get(session.id);
    lastStateRef.current.set(session.id, session.state);
    shownRef.current = session;
    shownSessionId = session.id;
    bridgeRef.current?.emit(sessionChangedMessage(session, standingRef.current.get(session.id)));
    // Waiting on the person, or no longer: a host with the panel tucked away shows it (ADR 0141).
    const attention = attentionMessage(was, session.state);
    if (attention) bridgeRef.current?.emit(attention);
    if (turnMoved(was, session.state)) {
      if (clientRef.current) void refreshStanding(clientRef.current, session.id);
      if (was === 'running') setTurnEnds((n) => n + 1);
    }
    // The conversation moved: an ask this frame is waiting to hear of may have started.
    if (was !== session.state && clientRef.current) void followWaitingAsks(clientRef.current, session.id);
  }, [refreshStanding]);
  /**
   * A part of the screen failed to draw. Already reported to the server and said in its place;
   * the host hears only of a failure that took the whole session off the screen, as
   * `chat42:error` with `case: 'render'`, which Rule42 shows as "The AI session hit a problem"
   * with Try again. A smaller one is not sent: the frame is still working, and a banner over a
   * working conversation is the thing the owner asked Rule42 to stop showing (ADR 0100).
   *
   * The sentence is generic on purpose. The protocol carries no conversation content, and an
   * error's message can quote some; the server's journal has the message.
   */
  const onFailure = useCallback((failure: Failure) => {
    if (!failure.blocking) return;
    bridgeRef.current?.emit(frameErrorMessage('This session could not be drawn.', { case: 'render' }));
  }, []);

  /** The signed-in client, for callbacks that outlive a render. */
  const clientRef = useRef<Chat42Client | null>(null);
  useEffect(() => {
    clientRef.current = state.phase === 'signed-in' ? state.client : null;
    reportingClient = clientRef.current;
  }, [state]);

  /** Where a session stands, asked for as soon as it is ready: the host's "on open". */
  useEffect(() => {
    if (state.phase !== 'signed-in' || !readySessionId) return;
    void refreshStanding(state.client, readySessionId);
  }, [state, readySessionId, refreshStanding]);

  /**
   * Put the open session on the model the host asked for, without a turn. Held while a turn
   * runs, because the runtime refuses a model change mid-turn, and tried as that turn ends.
   * The host is told the model its next turn will use afterwards, whatever happened, so its
   * header names the truth.
   */
  // The host's mode for the person, on the session it opened (ADR 0147). Not a turn, and allowed
  // mid-turn as the Mode chip is. A failure is said to the host, which keeps its own words for it.
  useEffect(() => {
    if (state.phase !== 'signed-in' || pendingMode === null || !readySessionId || readOnlyRef.current) return;
    const mode = pendingMode;
    const id = readySessionId;
    setPendingMode(null);
    void state.client.setSessionPermissionMode(id, mode).catch((err: unknown) => {
      bridgeRef.current?.emit(frameErrorMessage(`The mode was not changed: ${err instanceof Error ? err.message : String(err)}`));
    });
  }, [state, pendingMode, readySessionId]);

  useEffect(() => {
    if (state.phase !== 'signed-in' || pendingModel === null || !readySessionId) return;
    if (lastStateRef.current.get(readySessionId) === 'running') return;
    const model = pendingModel;
    const id = readySessionId;
    setPendingModel(null);
    void (async () => {
      try {
        await state.client.setSessionModel(id, model);
      } catch (err) {
        bridgeRef.current?.emit(frameErrorMessage(
          `The model was not changed: ${err instanceof Error ? err.message : String(err)}`,
        ));
      }
      await refreshStanding(state.client, id);
    })();
  }, [state, pendingModel, readySessionId, turnEnds, refreshStanding]);

  /**
   * Tell the ready session where the person is. Not a turn and not a message: the next turn is
   * told it as context. A failure is not said to the host: the place is context, the host sends
   * the next one on the next move, and a banner over a conversation for a missed one would
   * make a page that moves often look broken.
   */
  useEffect(() => {
    if (state.phase !== 'signed-in' || !location || !readySessionId) return;
    const client = state.client;
    const id = readySessionId;
    const context = location.context;
    locationChain.current = locationChain.current
      .then(() => client.setSessionLocation(id, context))
      .catch(() => undefined);
  }, [state, location, readySessionId]);

  /**
   * Hand the work the host asked for to the conversation's line on the server, in the order it
   * was asked, once the session is ready for it (ADR 0137). The server starts each piece once the
   * conversation is at rest, with its focus, model, preset and permission mode applied as it
   * starts, so nothing here waits for a turn or sets anything up first. The host is told where
   * each stands: `work-started` when it started at once, `work-queued` with its place when it
   * waits its turn.
   *
   * The code first, before the work is handed over: a run about a folder that could not be opened
   * is not asked for at all, and the host is told why in the attach's own case
   * (`machine-offline`, `folder-gone`), so its panel can say it in its own words. See
   * `attachHostCode`.
   */
  useEffect(() => {
    if (state.phase !== 'signed-in' || !readySessionId || !heldWorkRef.current.length) return;
    const client = state.client;
    const id = readySessionId;
    const batch = heldWorkRef.current.splice(0);
    const handOver = async ({ work, from, run, repository }: HeldWork): Promise<void> => {
      try {
        if (work.code) {
          const session = await client.findSession(id).catch(() => undefined);
          const attached = await attachHostCode(client, session ?? { id }, work.code);
          if (!attached.ok) {
            bridgeRef.current?.emit(frameErrorMessage(attached.message, { case: attached.case }));
            return;
          }
        }
        // Only ever wider; see `widenToolsets`.
        const focus = focusForWork(work.focus);
        const out = await askHostWork(client, id, {
          prompt: work.prompt,
          ...(work.label ? { label: work.label } : {}),
          ...(work.model ? { model: work.model } : {}),
          ...(work.preset ? { preset: work.preset } : {}),
          ...(work.permissionMode ? { permissionMode: work.permissionMode } : {}),
          ...(focus ? { focus } : {}),
          ...(run ? { run } : {}),
          ...(repository ? { repository } : {}),
          // A turn about something other than the code: every repository read-only for it.
          ...(readsCodeOnlyOf(work.readsCodeOnly) ? { readsCodeOnly: readsCodeOnlyOf(work.readsCodeOnly)! } : {}),
          from,
        });
        if (out.queued) {
          waitingAsksRef.current.set(out.id, { sessionId: id, position: out.position ?? 1 });
          setAsksWaiting((n) => n + 1);
        }
        // Shown where it runs or waits. The screen follows the host's last request, and a run
        // started in one session while another was on screen looked like nothing had started.
        setHostSessionId(id);
        bridgeRef.current?.emit(out.answer);
        // Started now on the model it asked for: the host's header is told so at once.
        if (!out.queued) void refreshStanding(client, id);
      } catch (err) {
        bridgeRef.current?.emit(frameErrorMessage(
          `The run was not started: ${err instanceof Error ? err.message : String(err)}`,
        ));
      }
    };
    handingRef.current = handingRef.current.then(async () => {
      for (const held of batch) await handOver(held);
    });
  }, [state, readySessionId, workHeld, refreshStanding]);

  /**
   * Asks this frame handed over that still wait, read again while any do: each that moved up is
   * told to the host with its new place, and each that started is told started (`followAsks`).
   * Read on every move of the conversation shown, and every `FOLLOW_ASKS_MS` as a backstop for a
   * conversation not on screen or a move the list did not show.
   */
  const followWaitingAsks = async (client: Chat42Client, sessionId: string): Promise<void> => {
    if (![...waitingAsksRef.current.values()].some((a) => a.sessionId === sessionId)) return;
    const line = await client.asks(sessionId).catch(() => undefined);
    if (!line) return;
    const { told, still } = followAsks(waitingAsksRef.current, sessionId, line);
    waitingAsksRef.current = still;
    for (const message of told) bridgeRef.current?.emit(message);
  };
  useEffect(() => {
    if (state.phase !== 'signed-in' || !waitingAsksRef.current.size) return;
    const client = state.client;
    const timer = setInterval(() => {
      const sessions = new Set([...waitingAsksRef.current.values()].map((a) => a.sessionId));
      if (!sessions.size) { clearInterval(timer); return; }
      for (const sessionId of sessions) void followWaitingAsks(client, sessionId);
    }, FOLLOW_ASKS_MS);
    return () => clearInterval(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state, asksWaiting]);

  useEffect(() => {
    if (state.phase !== 'signed-in' || wanted === null) return;
    const request = wanted;
    setWanted(null);
    // Failures are reported by `createForHost`; the host's own request has nobody here to tell.
    void createForHost(request).catch(() => undefined);
  }, [state, wanted, createForHost]);

  useEffect(() => {
    if (!isFramed()) return;
    setState({ phase: 'awaiting-host' });

    let bridge: Chat42FrameBridge | null = null;
    let stopped = false;

    void (async () => {
      const origins = await framedBy();
      if (stopped) return;
      if (!origins.length) {
        // Framed by somebody, with no host permitted. Said rather than waited on: this is a
        // deployment that has not been told who may embed it, and a frame that sat silent would
        // look like a slow load forever.
        setState({ phase: 'failed', message: 'This Chat42 is not configured to be embedded.' });
        return;
      }
      bridge = makeBridge(origins);
      bridgeRef.current = bridge;
      bridge.start();
    })();

    return () => { stopped = true; bridge?.stop(); bridgeRef.current = null; };
  }, []);

  /**
   * How the picker closed, as the one answer its request gets. Only the place goes to the host:
   * the machine's id and name and the folder's path, remote and branch, which are the person's
   * and which a host may keep for that person (the contract's own words).
   */
  const answerPick = useCallback((requestId: string, outcome: FolderPickOutcome) => {
    const message: FrameMessage = outcome.kind === 'picked'
      ? { type: 'chat42:folder-picked', requestId, ...outcome.folder }
      : outcome.kind === 'cancelled'
        ? { type: 'chat42:folder-pick-cancelled', requestId }
        : { type: 'chat42:folder-pick-failed', requestId, case: outcome.case };
    // Once per request: the picker answers once, and an answer for one already replaced is not sent.
    if (pickingRef.current?.requestId !== requestId) return;
    pickingRef.current = null;
    bridgeRef.current?.emit(message);
    setPicking(null);
  }, []);

  // A request that cannot be drawn is answered rather than left: a frame that did not sign in
  // has nobody whose machines to list.
  useEffect(() => {
    if (!picking) return;
    if (state.phase === 'failed' || state.phase === 'signed-out' || state.phase === 'open') {
      answerPick(picking.requestId, { kind: 'failed', case: 'other' });
    }
  }, [picking, state, answerPick]);

  /** The person's press of a button only the host can carry out: sent, and answered by its id (ADR 0147). */
  const hostAction = useCallback<HostAction>(({ action, label }) => new Promise<HostActionAnswer>((resolve) => {
    const bridge = bridgeRef.current;
    if (!bridge) { resolve({ ok: false, said: 'The page is not connected, so nothing was changed. Reload it and try again.' }); return; }
    const requestId = globalThis.crypto?.randomUUID?.() ?? `pa-${Date.now()}-${Math.random().toString(36).slice(2)}`;
    actionWaits.current.set(requestId, resolve);
    bridge.emit({ type: 'chat42:person-action', requestId, label, action });
  }), []);
  // Whatever is still waiting when the frame goes is answered, never left pressed.
  useEffect(() => () => {
    for (const wait of actionWaits.current.values()) wait({ ok: false, said: 'The page was closed before it answered. Nothing is known to have changed.' });
    actionWaits.current.clear();
  }, []);

  /** Said to a host that asked a read-only session to change: nothing was done. */
  const refuseReadOnly = () => bridgeRef.current?.emit(frameErrorMessage('This session is open read only. Nothing was changed.'));

  const makeBridge = (origins: string[]) =>
    new Chat42FrameBridge({
      allowedOrigins: origins,
      // What this frame has wired, announced in `ready`, so a host knows to wait on the person
      // rather than on a ten-second silence.
      features: [...FRAME_FEATURES],
      onPickFolder: (request) => {
        const was = pickingRef.current;
        if (was) bridgeRef.current?.emit({ type: 'chat42:folder-pick-cancelled', requestId: was.requestId });
        pickingRef.current = request;
        setPicking(request);
      },
      onInit: ({ token, surfaces, theme, view, assistant, replyCost, personActions }) => {
        setHostPersonActions(personActions === true);
        if (surfaces) setHostSurfaces(surfaces);
        if (theme) setHostTheme(theme);
        if (view) setHostView(view);
        if (assistant) setHostAssistant(assistant);
        if (replyCost) setHostReplyCost(replyCost);
        // A host token, not a stored session. `tokenSource` reads this app's own storage, which
        // a framed app never fills. Held, not captured: `chat42:token` replaces it, and the
        // client reads it on every request (ADR 0101). The client presents it as its gateway and
        // MCP tokens too, so the server holds the newest for the library and for a running turn.
        const held = hostToken(token);
        hostTokenRef.current = held;
        // Every Rule42 gateway on this page asks with it too, never with a session this origin
        // stored when somebody used Chat42 directly, which had long expired.
        setHostBearer(() => held.current());
        const client = new Chat42Client({
          baseUrl: window.location.origin,
          // With the entry context, whose `orgId` every request names as the host's organisation
          // (ADR 0103). Through the ref: the context may arrive after this.
          ...framedClientOptions(held, () => entryRef.current),
          // Through the ref: the bridge this belongs to is the one in scope when it fires.
          onUnauthenticated: () => bridgeRef.current?.emit({ type: 'chat42:token-expired' }),
        });
        setState({ phase: 'signed-in', client });
      },
      /*
       * A renewed token from the host: used by the next request, and handed to the server now,
       * so a turn that is already running, or waiting for a renewal, carries on with it rather
       * than with the token it started on. Before this the frame ignored the message, and a
       * framed client presented the token `init` carried until it was remounted.
       */
      onToken: (token) => {
        if (!hostTokenRef.current?.renew(token)) return;
        void clientRef.current?.offerTokens({ gateway: token, mcp: token });
      },
      // A new request for a session forgets the one that was ready: work, a model or a place
      // the host sends after it is meant for the session it is now asking for, not the last.
      onOpenSession: (sessionId, more) => {
        openMoreRef.current = more ?? {};
        readOnlyRef.current = more?.readOnly === true;
        setReadOnlyOpen(more?.readOnly === true);
        openGateRef.current.opening(more?.readOnly === true);
        setReadySessionId(null);
        setHostRequest('pending'); setOpenRequest(sessionId); setHostSessionId(sessionId);
      },
      onNewSession: (title, more) => {
        readOnlyRef.current = false;
        setReadOnlyOpen(false);
        openGateRef.current.settle();
        setReadySessionId(null);
        setHostRequest('pending');
        setWanted({
          title: title ?? '', ...(more?.model ? { model: more.model } : {}), ...(more?.origin ? { origin: more.origin } : {}),
          ...(more?.replace ? { replace: more.replace } : {}),
        });
      },
      // A session open read only takes none of these (ADR 0135): each would write to it.
      onSetModel: (model) => { if (readOnlyRef.current) refuseReadOnly(); else setPendingModel(model); },
      onLocation: (context) => { if (!readOnlyRef.current) setLocation({ context }); },
      // Every piece of work waits its turn in the conversation's one line on the server (ADR 0137).
      onStartWork: (work) => {
        if (readOnlyRef.current) { refuseReadOnly(); return; }
        holdWork({ from: 'host', work });
      },
      onEntryContext: (context) => { entryRef.current = context; setEntry(context); },
      onTheme: (theme) => setHostTheme(theme),
      onSetView: (view) => setHostView(view),
      onSetReplyCost: (replyCost) => setHostReplyCost(replyCost),
      onSetDetail: (opens) => setHostDetail(opens),
      onPersonActionResult: ({ requestId, ok, said }) => {
        const wait = actionWaits.current.get(requestId);
        actionWaits.current.delete(requestId);
        wait?.({ ok, said });
      },
      onSetPermissionMode: (mode) => { if (!readOnlyRef.current) setPendingMode(mode); },
      onSetFocus: (focus) => setHostFocus(focus),
    });

  useEffect(() => {
    // Not in a frame. Identity there is the host's, and this effect is the app's own sign-in:
    // left running, the two race. Whichever `setState` lands last decides which client the app
    // holds, and a stored Chat42 session from signing in directly will quietly win — leaving a
    // framed panel authenticated as itself, ignoring the token the host just handed over, and
    // creating nothing the host asked for.
    if (isFramed()) return;

    let cancelled = false;

    void (async () => {
      try {
        const config = await fetchAuthConfig();

        if (!config) {
          // A deployment with no issuer. Development, or behind another gate.
          if (!cancelled) setState({ phase: 'signed-in', client: build(null) });
          return;
        }

        // Unconditional: it returns undefined when this is not a callback.
        const completed = await completeSignIn(config);
        if (completed) {
          // The popup a page opened to sign in again: hand back, and close.
          if (finishSignInPopup()) return;
          // Strip the code and state from the URL before anything can log or
          // share it. Replace, not push, so Back does not re-run a used code.
          window.history.replaceState({}, '', completed.returnTo || '/');
        }

        // Back from turning something on elsewhere. The tokens held were issued
        // before the access was, so renew them all before anything asks with
        // them, then drop the marker so a reload does not renew again.
        const accessChanged = returnedWithNewAccess(window.location.search);
        if (accessChanged) {
          const url = new URL(window.location.href);
          url.searchParams.delete(ACCESS_CHANGED.param);
          url.searchParams.delete('auto');
          window.history.replaceState({}, '', `${url.pathname}${url.search}${url.hash}`);
          if (loadSession()) await renewAfterAccessChange(config).catch(() => undefined);
        }

        if (cancelled) return;
        if (!loadSession()) {
          setState({ phase: 'signed-out', config });
          return;
        }
        const client = build(config);
        setState({ phase: 'signed-in', client });
        if (accessChanged) {
          // What the new access adds: who they are now, the connection it
          // includes, and the organisation's library. Each is the page's own
          // loader, so a failure here is the one it would show anyway.
          void (async () => {
            await client.offerTokens(await workTokensFor(config));
            await Promise.allSettled([client.loadMe(), client.loadConnections(true), client.loadLibrary(true)]);
          })();
        }
      } catch (err) {
        if (!cancelled) {
          setState({ phase: 'failed', message: err instanceof Error ? err.message : String(err) });
        }
      }
    })();

    return () => { cancelled = true; };
  }, [build]);

  if (state.phase === 'starting') return <Centred>Starting…</Centred>;
  // Framed, before the host has spoken. Not a sign-in screen: the host owns the identity here,
  // and offering one inside a frame invites a second account for the same person.
  if (state.phase === 'awaiting-host') return <Centred>Connecting…</Centred>;

  if (state.phase === 'failed') {
    return (
      <Centred>
        <p style={{ marginBottom: 12 }}>{state.message}</p>
        <button style={buttonStyle} onClick={() => window.location.assign('/')}>Try again</button>
      </Centred>
    );
  }

  if (state.phase === 'signed-out') {
    // Framed, and somehow signed out: say so, and do not offer the button.
    //
    // Signing in means navigating this frame to the identity provider, which the host's
    // `frame-src` refuses — correctly, because framing somebody's login form is how credentials
    // get harvested. The press therefore does nothing visible and leaves a console error, which
    // is a worse answer than not offering it. The host owns the identity here: it reauthenticates
    // and hands over a fresh token.
    if (isFramed()) {
      return (
        <Centred>
          <p style={{ color: '#55606F' }}>
            This session needs signing in again. Reload the page it is embedded in.
          </p>
        </Centred>
      );
    }
    return (
      <Centred>
        <p style={{ marginBottom: 16, color: '#55606F' }}>Chat42 needs to know who you are.</p>
        <button style={buttonStyle} onClick={() => void beginSignIn(state.config)}>
          Sign in with Rule42
        </button>
        {/* Registration belongs to the issuer, not here. Chat42 running its own
            would mean its own password reset, its own verification and its own
            idea of an organisation — and a person would end up with a Chat42
            identity and a Rule42 identity that look the same and are not
            (ADR 0010). `app=chat42` is what makes the wizard wear this
            product's name and colours; `returnTo` brings them back. */}
        <p style={{ marginTop: 18, color: '#55606F' }}>
          New here?{' '}
          <a style={linkStyle} href={registerHref(state.config.issuer)}>Create an account</a>
        </p>
      </Centred>
    );
  }

  if (state.phase === 'open') return <Centred>Signed out.</Centred>;
  if (reopenFailed) {
    return (
      <Centred>
        <p style={{ marginBottom: 12, maxWidth: 420 }}>{reopenFailed.message}</p>
        <button style={buttonStyle} onClick={() => setOpenRequest(reopenFailed.id)}>Try again</button>
      </Centred>
    );
  }
  return (
    <SandboxDocumentCtx.Provider value={SANDBOX_DOCUMENT}>
      <PrintDocumentCtx.Provider value={PRINT_DOCUMENT}>
        <DiagramFontsCtx.Provider value={DIAGRAM_FONTS}>
          <FocusCtx.Provider value={focusChip}>
          <HostActionCtx.Provider value={isFramed() && hostPersonActions ? hostAction : undefined}>
          <Chat42
            client={state.client}
            {...(COMMIT ? { build: COMMIT } : {})}
            onFailure={onFailure}
            // Framed, the frame's own defaults under the host's: no account chip unless the host
            // asks for one, since the host's header already says who is signed in (frameSurfaces).
            // Applied even where the host sent no surfaces at all, which is when it matters most:
            // that host gets the whole app, rail included, and still owns the identity.
            {...(isFramed()
              ? { surfaces: readOnlyOpen ? { ...frameSurfaces(hostSurfaces), composer: false } : frameSurfaces(hostSurfaces) }
              : hostSurfaces ? { surfaces: hostSurfaces } : {})}
            {...(hostTheme ? { theme: hostTheme } : {})}
            {...(hostView ? { view: hostView } : {})}
            {...(hostAssistant ? { assistant: hostAssistant } : {})}
            {...(hostReplyCost ? { replyCost: hostReplyCost } : {})}
            {...(hostDetail === 'view' ? { onDetailView: askForDetail } : {})}
            {...(hostSessionId ? { sessionId: hostSessionId } : {})}
            {...(repositoryNames.length ? { repositoryNames } : {})}
            {...(workingProject ? { workingProject } : {})}
            connecting={isFramed() && hostConnecting(hostRequest, hostSurfaces)}
            onSessionChanged={onSessionChanged}
            // The same path the host's own `new-session` takes, so a session started from the
            // empty screen is titled for the host's record and knows it, rather than being a
            // second, nameless kind of session that happens to appear in the same panel.
            onNeedSession={() => createForHost({ title: entryRef.current['title'] ?? '' })}
          />
          </HostActionCtx.Provider>
          {picking && (
            <FolderPicker
              key={picking.requestId}
              client={state.client}
              {...(picking.hint ? { hint: picking.hint } : {})}
              {...(state.client.state.me?.userId ? { userId: state.client.state.me.userId } : {})}
              pairUrl={`${window.location.origin}${PAIR_PATH}`}
              {...(hostTheme ? { theme: hostTheme } : {})}
              onDone={(outcome) => answerPick(picking.requestId, outcome)}
            />
          )}
          </FocusCtx.Provider>
        </DiagramFontsCtx.Provider>
      </PrintDocumentCtx.Provider>
    </SandboxDocumentCtx.Provider>
  );
}

/**
 * Where somebody goes to sign up.
 *
 * Built from the issuer the page already fetched, so a deployment pointed at a
 * different auth server sends people to that one rather than to a hardcoded
 * address that is right exactly once.
 */
function registerHref(issuer: string): string {
  const url = new URL('/auth/register', issuer);
  // No returnTo. The issuer only accepts a path under /auth/, so anything
  // pointing back here would be refused — and `app=chat42` already tells it
  // where "done" leads, which is this application rather than a login page
  // belonging to something else.
  url.searchParams.set('app', 'chat42');
  return url.toString();
}

const linkStyle: React.CSSProperties = { color: '#4F46E5', fontWeight: 500 };

const buttonStyle: React.CSSProperties = {
  font: 'inherit', padding: '8px 14px', borderRadius: 5,
  border: '1px solid #4F46E5', background: '#4F46E5', color: '#fff', cursor: 'pointer',
};

function Centred({ children }: { children: React.ReactNode }) {
  return (
    <div style={{
      height: '100%', display: 'grid', placeContent: 'center', textAlign: 'center',
      font: "14px/1.5 'IBM Plex Sans', system-ui, sans-serif", color: '#0F172A',
    }}>
      <div>
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8, fontWeight: 600, marginBottom: 10 }}>
          <Chat42Mark size={28} />
          Chat42
        </div>
        {children}
      </div>
    </div>
  );
}

/**
 * Above everything, for a failure in this page's own code: the sign-in, the host bridge, the
 * screens drawn before there is a client. `Chat42` has a boundary of its own that redraws the
 * session in place; this one is above it and sees only what that one cannot reach, where nothing
 * is left to redraw, so its Reload loads the page again. In a host's frame that is still a way
 * back: the frame says it is ready again and the host answers as it did the first time.
 */
function PageFailure() {
  return (
    <Centred>
      <p style={{ marginBottom: 12 }}>Something went wrong.</p>
      <button style={buttonStyle} onClick={() => window.location.reload()}>Reload</button>
    </Centred>
  );
}

const el = document.getElementById('root');
if (!el) throw new Error('No #root');
createRoot(el).render(
  <FailureSinkCtx.Provider value={reportPageFailure}>
    <Boundary area="page" blocking fallback={() => <PageFailure />}>
      <App />
    </Boundary>
  </FailureSinkCtx.Provider>,
);
